import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, Dropdown, RoundSkeleton, Tooltip } from '@treezor/design-system';

import { TComputedKycDocument } from '@/features/kyc/store/models/kyc.model';
import { useKycDocumentActions } from '@/features/kyc/hooks/useKycDocumentActions';

type TKycDocumentActionProps = {
  document: TComputedKycDocument;
  isLoading?: boolean;
};

export function KycDocumentActions({ document, isLoading: isRefreshing }: TKycDocumentActionProps) {
  const { t } = useTranslation();
  const { isMenuOpen, toggleMenu, dropdownActions, isDisabled, isLoading, viewKycDocument } = useKycDocumentActions({
    document,
  });
  const ref = useRef<HTMLAnchorElement & HTMLButtonElement>(null);

  if (isRefreshing) {
    return (
      <div className="flex items-center gap-3">
        <RoundSkeleton size={16} margin="2px" />
        <RoundSkeleton size={16} margin="2px" />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <IconButton
        ref={ref}
        icon="more_vert"
        disabled={dropdownActions.length === 0}
        size="s"
        variant="secondary"
        onClick={toggleMenu}
      />
      {isDisabled ? (
        <Tooltip
          messageContent={t('pages.currentProfiles.kyc.documents.preview.notAvailable')}
          contentClassName="min-w-[100px]"
        >
          <IconButton icon="chevron_right" disabled={true} size="s" variant="secondary" />
        </Tooltip>
      ) : (
        <IconButton
          icon="chevron_right"
          disabled={isDisabled}
          isLoading={isLoading}
          size="s"
          variant="secondary"
          onClick={viewKycDocument}
        />
      )}
      {isMenuOpen && (
        <Dropdown onClose={toggleMenu} parentRef={ref} className="!w-min">
          <div className="flex flex-col py-3">
            {dropdownActions.map((action) => (
              <div
                className="px-4 py-3 typo-body-regular-paragraph text-primary text-nowrap hover:bg-secondary hover:border-interactive hover:cursor-pointer"
                key={`action-${action.key}`}
                onClick={() => {
                  action?.onClick();
                  toggleMenu();
                }}
              >
                {t(`pages.currentProfiles.kyc.documents.actions.${action.key}`)}
              </div>
            ))}
          </div>
        </Dropdown>
      )}
    </div>
  );
}
